import { StaticImage } from "gatsby-plugin-image"

const ifeatData = {
  color: "#c6c3ff",
  heroTitle: `How IFEAT created a branded, high-touch attendee experience with Premagic`,
  heroImage: (
    <StaticImage src="../../images/case-study/ifeat/hero.png" alt="IFEAT Annual Conference case study hero" />
  ),
  metrics: {
    quote: `
      Table of Contents<br/><br/>
      <ul style="list-style-type: disc; padding: 0 60px; text-align: left;">
        <li style="margin-bottom: 16px;"><a href='#section-0'>IFEAT’s challenge: Delivering measurable value without compromising the brand</a></li>
        <li style="margin-bottom: 16px;"><a href='#section-1'>The strategy: A branded, high-touch attendee experience</a></li>
        <li style="margin-bottom: 16px;"><a href='#section-2'>Features used</a></li>
        <li class='mb-2'><a href='#section-3'>The impact: Real connections, real-time content, real value</a></li>
        <li class='mb-2'><a href='#section-4'>The result: From cautious trial to continued collaboration</a></li>
      </ul>`,
  },
  images: [
    {
      img: (
        <StaticImage
          src="../../images/case-study/ifeat/banner.jpeg"
          alt="Banner"
        />
      ),
    },
    {
      img: (
        <StaticImage
          src="../../images/case-study/ifeat/banner2.jpeg"
          alt="Banner"
        />
      ),
    },
  ],
  gallery: (
    <StaticImage
      src="../../images/case-study/ifeat/collage.jpg"
      alt="gallery"
    />
  ),
  caseStudyData: [
    {
      title:
        "IFEAT’s challenge: Delivering measurable value without compromising the brand",
      info1: `<b>IFEAT (International Federation of Essential Oils and Aroma Trades)</b> is a global network of professionals across the fragrance and flavour supply chain. Every year, they bring their international community together at a flagship conference.<br/><br/>
      This partnership, like many others, began with a referral. Premagic isn’t just a tool; it’s something teams want others to use once they’ve tried it themselves. That led to initial conversations with IFEAT’s team based in the UK, headed by Shehzad Chaudhry.<br/><br/>
      The IFEAT team was particularly intrigued by the <a href='https://premagic.com/smart-photo-distribution-for-seamless-events/'>AI photo distribution feature</a>. They had never employed any such technology at their event in the past and were quite keen to try it at their annual conference being held in Bangkok that year.<br/><br/>However, there was healthy skepticism, especially around ROI. With nearly 3,000 attendees, this was a large-scale event and the IFEAT team needed to be sure that any new tool would justify its value without adding operational complexity.<br/><br/>Additionally, ​branding was also critical. From registration reminders to final photo delivery, they wanted their brand to be at the center.<br/><br/>And lastly, with a deeply engaged member base, they were keen on strengthening community connections, especially on social media by helping members connect with each other.`,
      // testimonial: {
      //   img: (
      //     <StaticImage src="../../images/case-study/idc/avatar.jpeg" alt="" />
      //   ),
      //   message: `“IDC came in with a clear goal. They wanted engagement that felt organic, not forced. Once they saw how Premagic could blend creativity with automation, the hesitation disappeared.“`,
      //   name: "Bhakti Kapasi",
      //   info: `Account Executive,<br/> Premagic`,
      // },
      // inlineLink: {
      //   text: 'BREAKING STEREOTYPES AT THE BANKNOTE CONFERENCE',
      //   link: 'https://premagic.com/case-study/currency-research',
      // }
    },
    {
      title: "The strategy: A branded, high-touch attendee experience",
      info1: `We addressed the team’s concerns by offering detailed support and examples. We shared demo videos, use cases, and information on how AI photo distribution worked at similar events, which helped the team gain confidence in the product.
      <br/><br/>Premagic partnered with IFEAT to offer a tightly integrated experience using these key modules:<br/><br/>
      <ol style="list-style-type: decimal; padding-left: 40px;">
        <li style="margin-bottom: 26px;">
          <strong>Photos delivered instantly</strong><br>
          A feature that the team had high expectations for. With <a href='https://premagic.com/smart-photo-distribution-for-seamless-events/'>photo distribution</a>, all the attendees at IFEAT’s annual conference registered with a selfie and received all their event photos in real time via WhatsApp or email. The team didn’t have to manage folders or follow-ups - photos were tagged and shared automatically, keeping the momentum going during the event.
        </li>
        
        <li style="margin-bottom: 26px;">
          <strong>Smart networking opportunities</strong><br>
          With hundreds of global members attending, Premagic’s <a href='https://premagic.com/event-networking-app/'>Ripple</a> made it easier to discover who was in the room. Attendees could easily connect with others by tapping on any face in the photo gallery to instantly access attendee information.
        </li>
        
        <li style="margin-bottom: 26px;">
          <strong>Watermarking for better brand visibility</strong><br>
          As a part of increasing their brand visibility, IFEAT also opted for the watermarking feature wherein every photo was watermarked with the event branding, helping extend visibility in an organic way.
        </li>
        
        <li style="margin-bottom: 26px;">
          <strong>A fully white-labeled experience</strong><br>
          The experience was fully white-labeled, delivered from IFEAT’s own domain, making it feel like a native part of the event. The WhatsApp integration ensured that each notification sent to the attendees came from IFEAT’s official account.
        </li>
      </ol>
      Want to see how branded photo delivery could elevate your event? <br/><br/>
      <a href='https://premagic.com/request-a-demo?utm_source=ifeat_casestudy&utm_medium=blog&utm_campaign=case_cta_1'>Get a free demo</a>
`,
    },
    // {
    //   title: 'Features Used:',
    //   info1: `<ul style="list-style-type: disc; padding-left: 20px">
    //   <li class='mb-2'><a href='https://premagic.com/smart-photo-distribution-for-seamless-events/'>AI-powered Photo Distribution</a></li>
    //   <li class='mb-2'><a href='https://premagic.com/event-networking-app/'>Networking Module (Ripple)</a></li>
    //   <li class='mb-2'><a href='https://premagic.com/sponsored-content/'>Sponsor Watermark Module</a></li>
    //   <li class='mb-2'><a href='javascript:void(0)'>Digital Asset Management</a></li>
    //   <li class='mb-2'><a href='https://blog.premagic.com/sparks/'>Sparks - Event Memories</a></li>
    //   <li class='mb-2'><a href='javascript:void(0)'>White-labeled platform for all event touchpoints</a></li>
    //   <li class='mb-2'><a href='javascript:void(0)'>Post-event analytics and reporting</a></li>
    //   <li class='mb-2'><a href='javascript:void(0)'>Designated CXM for event setup with extensive support (24/7 phone, chats, and emails)</a></li>

    //   </ul>`,
    //   info2: ``,

    //   gridViewTitle: 'Key Features That Made It Work',
    //   inlineLink: {
    //     text: 'RETHINKING I-AM-ATTENDING POSTERS FOR SAASBOOMI ANNUAL 2025',
    //     link: 'https://blog.premagic.com/rethinking-i-am-attending-posters-for-saasboomi-annual-2025/',
    //   },
    // },
    {
      title: "The impact: Real connections, real-time content, real value",
      info1: `The results were clear: attendees shared their photos widely, members connected with new peers, and sponsors got visibility that felt integrated, not interruptive.<br/><br/>Over <b>4,000 professional photos</b> were automatically matched and sent to attendees in real time via WhatsApp or email, at a <b>delivery rate of 92.3%</b>.<br/><br/>A total of 2,939 social shares, across platforms like WhatsApp, DMs, and social media, helped the content travel far, boosting brand visibility and driving ROI of $17,905.* (*Based on average earned media value from organic social shares and estimated impressions.)<br/><br/><a href='https://premagic.com/event-networking-app/'>Ripple</a> added meaningful value to all the members who found and connected with their peers and continued conversations post-event. More importantly, the IFEAT team was able to trial an entirely new experience format without adding operational overhead. What began as a pilot turned into a longer-term conversation about how Premagic could support future events.`,
      inlineLink: {
        text: "See how personalized content can drive ROI for your event.",
        link: "https://premagic.com/case-study/gla-global",
      },
      image: (
        <StaticImage
          src="../../images/case-study/ifeat/banner2.jpeg"
          alt="Impact"
        />
      ),
    },
    {
      title: "The result: From cautious trial to continued collaboration",
      info1: `What started as an interest in <a href='https://premagic.com/smart-photo-distribution-for-seamless-events/'>photo distribution</a> - albeit wrapped in some early skepticism around ROI, turned into a full-fledged partnership.<br/><br/>IFEAT ended up using a lot more than just <a href='https://premagic.com/smart-photo-distribution-for-seamless-events/'>photo distribution</a>, adding on an extensive feature set to transform their event into a truly branded and connected experience. From <a href='https://premagic.com/event-networking-app/'>Ripple</a> networking and AI photo delivery to sponsor tools and white-labeling, Premagic helped IFEAT modernize their experience while keeping complete control over their brand narrative.<br/><br/>And this is just the beginning. The IFEAT team is already exploring new ways to use Premagic across upcoming conferences.<br/><br/>Sometimes, all it takes is one good event.<br/><br/>Are you ready to make yours unforgettable?`,
    },
  ],
  isCTAActive: true,
}

export default ifeatData
